import GoogleIcon from '@mui/icons-material/Google'
import { Button, CircularProgress, SxProps, Theme } from '@mui/material'
import React, { useCallback, useState } from 'react'
import { useGoogleSignInMutation } from '../../services/authApi'
import { GoogleUserData } from '../../types/auth'
import { GoogleSignInResponse } from '../../types/googleSignIn'
import { logError } from '../../utils/logError'

interface GoogleSignInButtonProps {
    onSuccess?: (userData: GoogleUserData) => void
    onError?: (error: Error) => void
    buttonText?: string
    fullWidth?: boolean
    className?: string
    sx?: SxProps<Theme>
    startIcon?: React.ReactNode
}

const GoogleSignInButton: React.FC<GoogleSignInButtonProps> = ({
    onSuccess,
    onError,
    buttonText = 'Google',
    fullWidth = true,
    className = '',
    sx = {},
    startIcon,
    ...props
}) => {
    const [isLoading, setIsLoading] = useState(false)
    const [googleSignIn] = useGoogleSignInMutation<GoogleSignInResponse>()
    const [error, setError] = useState<string | null>(null)

    const loadGoogleScript = useCallback((): Promise<void> => {
        return new Promise((resolve, reject) => {
            if (window.google?.accounts?.oauth2) {
                return resolve()
            }

            const script = document.createElement('script')
            script.src = 'https://accounts.google.com/gsi/client'
            script.async = true
            script.defer = true
            script.onload = () => {
                if (!window.google?.accounts?.oauth2) {
                    reject(new Error('Google Sign-In script loaded but API not available'))
                    return
                }
                resolve()
            }
            script.onerror = () => reject(new Error('Failed to load Google Sign-In script'))
            document.body.appendChild(script)
        })
    }, [])

    const handleGoogleSignIn = useCallback(
        async (e?: React.MouseEvent) => {
            e?.preventDefault()
            e?.stopPropagation()

            try {
                setIsLoading(true)
                setError(null)

                await loadGoogleScript()

                // @ts-ignore - Google types are available after script loads
                const client = window.google.accounts.oauth2.initCodeClient({
                    client_id: process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID || '',
                    scope: 'email profile',
                    callback: async (response: { code: string }) => {
                        try {
                            if (!response.code) {
                                throw new Error('No authorization code received from Google')
                            }

                            // Make the API call with the correct request structure
                            const result = await googleSignIn({
                                code: response.code,
                                dmType: 'flyin.com',
                                mediaType: 'GooglePlus'
                            }).unwrap()

                            if (result.statusCode === 200) {
                                console.log('Google Sign-In API Response:', result)

                                // DON'T process auth response here - let the parent component handle it
                                // This avoids double processing
                                const googleUserData: GoogleUserData = {
                                    email: result.registerEmailId,
                                    firstName: result.customerBasicDetails?.firstName || '',
                                    lastName: result.customerBasicDetails?.lastName || '',
                                    fullName:
                                        `${result.customerBasicDetails?.firstName || ''} ${result.customerBasicDetails?.lastName || ''}`.trim(),
                                    googleId: result.userUniqueId,
                                    userId: result.userUniqueId,
                                    ctAuth: result.ctAuth,
                                    mobile: result.mobileNumber || undefined,
                                    countryCode: result.mobileAreaCode || undefined
                                }

                                // Call onSuccess with the user data
                                onSuccess?.(googleUserData)
                            } else {
                                throw new Error(result.message || 'Failed to sign in with Google')
                            }
                        } catch (error) {
                            logError('GoogleSignInButton:callback', error)
                            onError?.(new Error('Failed to process Google Sign-In. Please try again.'))
                            onError?.(error instanceof Error ? error : new Error('Unknown error'))
                        } finally {
                            setIsLoading(false)
                        }
                    }
                })

                client.requestCode()
            } catch (error) {
                logError('GoogleSignInButton:init', error)
                onError?.(new Error('Failed to initialize Google Sign-In. Please try again later.'))
                onError?.(error instanceof Error ? error : new Error('Unknown error'))
                setIsLoading(false)
            }
        },
        [googleSignIn, loadGoogleScript, onError, onSuccess]
    )

    const buttonSx: SxProps<Theme> = {
        mb: 2,
        // width: '250px',
        height: '45px', // Increased height
        textTransform: 'none',
        backgroundColor: '#DB4437', // Google red color
        color: 'white',
        border: 'none',
        boxShadow: '0 2px 4px 0 rgba(0,0,0,.25)',
        '&:hover': {
            backgroundColor: '#C53929', // Slightly darker red on hover
            boxShadow: '0 4px 8px 0 rgba(0,0,0,0.2)'
        },
        ...sx
    }

    return (
        <div className={className}>
            {error && <div className="text-red-500 text-sm">{error}</div>}
            <Button
                variant="contained"
                fullWidth={fullWidth}
                onClick={handleGoogleSignIn}
                disabled={isLoading}
                startIcon={isLoading ? <CircularProgress size={20} /> : startIcon || <GoogleIcon />}
                sx={buttonSx}
                {...props}
            >
                {buttonText}
            </Button>
        </div>
    )
}

export default GoogleSignInButton
